import { apiFetch } from "../apiClient";
import {
  FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS,
  FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
  FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES,
  FACTSET_ROUTES,
} from "./factsetConstants";
import { isAbortError } from "../utils/abortError";

export interface CompanyLookupCandidate {
  rank: number;
  id: string;
  name: string;
  matchedName?: string;
  ticker?: string;
  companyType?: "public" | "private";
  country?: string;
}

export interface CompanyLookupResult {
  success: boolean;
  searchText: string;
  candidates: CompanyLookupCandidate[];
  error?: string;
}

function withTimeout(
  signal: AbortSignal | undefined,
): { signal: AbortSignal; cleanup: () => void; didTimeout: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) controller.abort(signal.reason);
  else signal?.addEventListener("abort", onAbort, { once: true });
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS);
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
    },
    didTimeout: () => timedOut,
  };
}

export async function lookupFactSetCompany(
  searchText: string,
  signal?: AbortSignal,
): Promise<CompanyLookupResult> {
  const normalized = searchText.trim();
  if (!normalized) {
    return {
      success: false,
      searchText: normalized,
      candidates: [],
      error: "Company lookup requires a non-empty company name or identifier.",
    };
  }
  if (
    new TextEncoder().encode(normalized).byteLength >
    FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES
  ) {
    return {
      success: false,
      searchText: normalized,
      candidates: [],
      error: `Company lookup text exceeds ${FACTSET_ID_LOOKUP_SEARCH_TEXT_MAX_BYTES} UTF-8 bytes.`,
    };
  }
  const timeout = withTimeout(signal);
  try {
    const response = await apiFetch(FACTSET_ROUTES.COMPANY_LOOKUP, {
      method: "POST",
      cache: "no-store",
      signal: timeout.signal,
      body: JSON.stringify({
        search_text: normalized,
        filters: null,
        limit: FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
      }),
    });
    const body = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      return {
        success: false,
        searchText: normalized,
        candidates: [],
        error:
          typeof body.message === "string"
            ? body.message
            : `FactSet company lookup failed (${response.status}).`,
      };
    }
    const rawCandidates = Array.isArray(body.candidates) ? body.candidates : [];
    const candidates = rawCandidates.flatMap((entry): CompanyLookupCandidate[] => {
      if (!entry || typeof entry !== "object") return [];
      const value = entry as Record<string, unknown>;
      if (
        !Number.isInteger(value.rank) ||
        typeof value.id !== "string" ||
        !value.id ||
        typeof value.name !== "string"
      ) {
        return [];
      }
      return [{
        rank: value.rank as number,
        id: value.id,
        name: value.name,
        ...(typeof value.matched_name === "string"
          ? { matchedName: value.matched_name }
          : {}),
        ...(typeof value.ticker === "string" ? { ticker: value.ticker } : {}),
        ...(value.company_type === "public" || value.company_type === "private"
          ? { companyType: value.company_type }
          : {}),
        ...(typeof value.country === "string" ? { country: value.country } : {}),
      }];
    });
    return {
      success: body.success === true && candidates.length > 0,
      searchText: normalized,
      candidates,
      ...(!candidates.length && typeof body.error === "string"
        ? { error: body.error }
        : {}),
    };
  } catch (error) {
    const message = isAbortError(error)
      ? timeout.didTimeout()
        ? "FactSet company lookup timed out."
        : "FactSet company lookup was cancelled."
      : error instanceof Error
        ? error.message
        : String(error);
    return {
      success: false,
      searchText: normalized,
      candidates: [],
      error: message,
    };
  } finally {
    timeout.cleanup();
  }
}
